import React, {useCallback, useState} from "react";
import {LinkAccountConfirmModal} from "./LinkAccountConfirmModal";
import {LinkAccountCancelledError, isLinkConfirmationRequiredError} from "../helpers/linkAccountError";

export type LinkAccountRequestInfo = {
    targetEmail: string;
    bookerName: string;
    previousEmail?: string | null;
    excludeBookerId?: string;
    variant?: "link" | "merge";
};

type PendingRequest = LinkAccountRequestInfo & {confirm: () => void; cancel: () => void};

/**
 * Bestätigungsfluss „Zugang verknüpfen“ für jede Mutation, die den Marker
 * `[linkAccount]confirmationRequired` liefern kann (createBooker, updateBooker, mergeBookers,
 * addFaUserByBookerId). `run(false)` wird zuerst gesendet; verlangt der Server die Bestätigung,
 * öffnet sich der Dialog und `run(true)` sendet dieselben Variablen mit
 * `linkExistingAccountConfirmed: true`. Abbrechen wirft {@link LinkAccountCancelledError}.
 */
export const useLinkAccountConfirm = () => {
    const [pending, setPending] = useState<PendingRequest | null>(null);

    const withLinkConfirmation = useCallback(async <T,>(info: LinkAccountRequestInfo, run: (confirmed: boolean) => Promise<T>): Promise<T> => {
        try {
            return await run(false);
        } catch (error) {
            if (!isLinkConfirmationRequiredError(error)) throw error;

            return new Promise<T>((resolve, reject) => {
                setPending({
                    ...info,
                    confirm: () => {
                        setPending(null);
                        run(true).then(resolve, reject);
                    },
                    cancel: () => {
                        setPending(null);
                        reject(new LinkAccountCancelledError());
                    },
                });
            });
        }
    }, []);

    const linkAccountModal = (
        <LinkAccountConfirmModal
            open={pending !== null}
            targetEmail={pending?.targetEmail ?? ""}
            bookerName={pending?.bookerName ?? ""}
            previousEmail={pending?.previousEmail}
            excludeBookerId={pending?.excludeBookerId}
            variant={pending?.variant}
            onConfirm={() => pending?.confirm()}
            onCancel={() => pending?.cancel()}
        />
    );

    return {linkAccountModal, withLinkConfirmation, linkAccountDialogOpen: pending !== null};
};
